import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Menu, LogOut, LayoutDashboard, Clapperboard, ShieldCheck, Radio, X, ShoppingBag, Bell, ChevronDown, LifeBuoy, Video as VideoIcon, Sparkles, UserCog, Gift, Trophy, Link as LinkIcon, Tv as TvIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useKidMode } from "../context/KidModeContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "./ui/sheet";
import NotificationBell from "./NotificationBell";
import api from "../lib/api";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";

// Primary nav (always visible on desktop). `hideOnMd: true` items are
// hidden until 2xl (1536px+) so on common laptop widths (1280–1440)
// the avatar/cart/Go-Live CTAs always stay reachable. Anything hidden
// here is still in the avatar dropdown + the Sheet menu, so users
// don't lose access.
const PRIMARY_NAV = [
  { to: "/", label: "Home" },
  { to: "/watch", label: "Watch" },
  { label: "Movies", dropdown: "movies" },
  { label: "Music",  dropdown: "music" },
  { to: "/live", label: "Live" },
  { to: "/radio", label: "Radio" },
  { to: "/browse/classics", label: "Free Movies", hideOnMd: true },
  { to: "/tv-guide", label: "TV Guide", hideOnMd: true },
  { to: "/social", label: "Community", hideOnMd: true },
  { to: "/dashboard", label: "My Channel", hideOnMd: true },
  { to: "/kids", label: "Kids", iconOnly: true },
];

// Subcategory dropdowns shown under the Movies / Music primary tabs
const MOVIES_SUBCATS = [
  { to: "/browse/films",            label: "All Movies" },
  { to: "/originals",               label: "Tap IN Originals" },
  { to: "/browse/films",            label: "Independent Films" },
  { to: "/browse/trailers",         label: "Trailers" },
  { to: "/browse/behind_the_scenes",label: "Behind the Scenes" },
  { to: "/browse/classics",         label: "Free Classics" },
  { to: "/browse/family",           label: "Family" },
  { to: "/browse/live_events",      label: "Live Events" },
  { to: "/browse/cartoons",         label: "Cartoons" },
  { to: "/browse/sports",           label: "Sports" },
  { to: "/browse/news",             label: "News & Documentary" },
];
const MUSIC_SUBCATS = [
  { to: "/browse/music",     label: "All Music" },
  { to: "/radio",            label: "Tap IN Radio (Live)" },
  { to: "/browse/gospel",    label: "Gospel" },
  { to: "/browse/podcasts",  label: "Interviews & Podcasts" },
  { to: "/browse/educational", label: "Educational" },
];// Items tucked into the "More" dropdown to keep the header tidy
const MORE_NAV = [
  { to: "/tv", label: "TV App", icon: TvIcon },
  { to: "/shorts", label: "Stories" },
  { to: "/shop", label: "Shop" },
  { to: "/dashboard", label: "My Channel" },
  { to: "/tap-in-news", label: "News" },
  { to: "/go-live", label: "Go Live Guide" },
  { to: "/browse/podcasts", label: "Podcasts" },
  { to: "/awards", label: "Awards" },
  { to: "/sponsors", label: "Sponsor us" },
  { to: "/contact", label: "Contact" },
  { to: "/support", label: "Tech Support", icon: LifeBuoy },
];

// Extra "More" items gated to premium_creator/admin
const MORE_NAV_PREMIUM = [
  { to: "/conference", label: "Conferences", icon: VideoIcon },
];

// Mobile sheet shows everything without duplicates. Dropdown primaries
// (Movies/Music) are expanded into their full subcategory list so the
// mobile drawer matches what desktop reveals on hover.
const PRIMARY_NAV_FLAT = PRIMARY_NAV.flatMap((n) => {
  if (n.dropdown === "movies") return MOVIES_SUBCATS.map((s) => ({ ...s, label: s.label }));
  if (n.dropdown === "music")  return MUSIC_SUBCATS.map((s) => ({ ...s, label: s.label }));
  return [n];
});
const ALL_NAV = [...PRIMARY_NAV_FLAT, ...MORE_NAV.filter((m) => !PRIMARY_NAV_FLAT.some((p) => p.to === m.to))];

export default function Header() {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const { lang, setLang, languages } = useI18n();
  const kidMode = useKidMode();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [mobileSearch, setMobileSearch] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      nav(`/search?q=${encodeURIComponent(q.trim())}`);
      setMobileSearch(false);
      setSheetOpen(false);
    }
  };

  const role = user?.role;

  return (
    <header
      data-testid="app-header"
      className="fixed top-0 inset-x-0 z-50 bg-gradient-to-b from-black/95 via-black/75 to-transparent backdrop-blur-md"
      style={{ paddingTop: "var(--safe-top, 0px)" }}
    >
      {kidMode.active && (
        <Link
          to="/kids"
          data-testid="kid-mode-strip"
          className="block w-full bg-gradient-to-r from-pink-500 via-tv-accent to-yellow-300 text-black text-[11px] font-bold uppercase tracking-wider text-center py-1"
        >
          🔒 Kid Mode is ON — only Kids content visible · tap to manage
        </Link>
      )}
      <div className="flex items-center gap-2 md:gap-4 px-3 sm:px-6 md:px-8 py-2 md:py-3">
        {/* Mobile menu trigger */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-menu-trigger"
              className="md:hidden w-10 h-10 rounded-full tv-glass flex items-center justify-center text-white shrink-0"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="bg-[#0A0A0A] border-r border-white/10 text-white w-[85vw] max-w-sm p-0 flex flex-col">
            <SheetHeader className="p-5 border-b border-white/10 shrink-0">
              <SheetTitle className="flex items-center gap-3">
                <img src="/tapintv-logo-v2.png" alt="Tap IN TV" className="h-14 w-auto" />
                <span className="font-display font-black text-lg text-white">Tap IN TV</span>
              </SheetTitle>
            </SheetHeader>
            <nav className="p-4 flex flex-col gap-1 flex-1 overflow-y-auto overscroll-contain pb-24">
              {(role === "creator" || role === "premium_creator" || role === "admin") && (
                <Link
                  to="/studio/live"
                  onClick={() => setSheetOpen(false)}
                  data-testid="mobile-nav-go-live"
                  className="px-4 py-3 rounded-lg text-base font-bold text-white bg-tv-live flex items-center gap-2 shadow-lg shadow-red-500/20"
                >
                  <Radio className="w-4 h-4 animate-pulse" /> Go Live
                </Link>
              )}
              {(role === "creator" || role === "premium_creator" || role === "admin") && (
                <Link
                  to="/reels/create"
                  onClick={() => setSheetOpen(false)}
                  data-testid="mobile-nav-create-reel"
                  className="px-4 py-3 rounded-lg text-base font-bold text-black bg-tv-accent flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" /> Create Reel
                </Link>
              )}
              {ALL_NAV.map((n) => (
                <Link
                  key={`${n.to}-${n.label}`}
                  to={n.to}
                  onClick={() => setSheetOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-4 py-3 rounded-lg text-base font-semibold transition flex items-center gap-2 ${
                    loc.pathname === n.to ? "bg-tv-accent text-black" : "text-white/85 hover:bg-white/5"
                  }`}
                >
                  {n.label === "Kids" && (
                    <img src="/tapintv-kids-logo.png" alt="" className="h-7 w-auto" />
                  )}
                  {n.label}
                </Link>
              ))}
              {(role === "creator" || role === "premium_creator" || role === "admin") && MORE_NAV_PREMIUM.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setSheetOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-4 py-3 rounded-lg text-base font-semibold transition flex items-center gap-2 ${
                    loc.pathname === n.to ? "bg-tv-accent text-black" : "text-white/85 hover:bg-white/5"
                  }`}
                >
                  <VideoIcon className="w-4 h-4 text-tv-accent" />
                  {n.label}
                </Link>
              ))}
              <div className="h-px bg-white/10 my-3" />
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setSheetOpen(false)} className="px-4 py-3 rounded-lg text-white/85 hover:bg-white/5 flex items-center gap-3">
                    <LayoutDashboard className="w-4 h-4" /> My Dashboard
                  </Link>
                  <Link to="/referrals" onClick={() => setSheetOpen(false)} data-testid="header-referrals-link" className="px-4 py-3 rounded-lg text-white/85 hover:bg-white/5 flex items-center gap-3">
                    <Gift className="w-4 h-4 text-tv-accent" /> Invite & Earn
                  </Link>
                  <Link to="/talent-search" onClick={() => setSheetOpen(false)} data-testid="header-talent-search-link" className="px-4 py-3 rounded-lg text-white/85 hover:bg-white/5 flex items-center gap-3">
                    <Trophy className="w-4 h-4 text-tv-accent" /> $50k Talent Search
                  </Link>
                  {role === "viewer" && (
                    <Link to="/apply-creator" onClick={() => setSheetOpen(false)} className="px-4 py-3 rounded-lg text-white/85 hover:bg-white/5 flex items-center gap-3">
                      <Clapperboard className="w-4 h-4" /> Become a Creator
                    </Link>
                  )}
                  {(role === "creator" || role === "premium_creator" || role === "admin") && (
                    <Link to="/studio" onClick={() => setSheetOpen(false)} className="px-4 py-3 rounded-lg text-white/85 hover:bg-white/5 flex items-center gap-3">
                      <Clapperboard className="w-4 h-4" /> Creator Studio
                    </Link>
                  )}
                  {(role === "creator" || role === "premium_creator" || role === "admin") && (
                    <Link to="/studio/live" onClick={() => setSheetOpen(false)} className="px-4 py-3 rounded-lg text-white/85 hover:bg-white/5 flex items-center gap-3">
                      <Radio className="w-4 h-4 text-tv-live" /> Director's Mode
                    </Link>
                  )}
                  {role === "admin" && (
                    <Link to="/admin" onClick={() => setSheetOpen(false)} className="px-4 py-3 rounded-lg text-white/85 hover:bg-white/5 flex items-center gap-3">
                      <ShieldCheck className="w-4 h-4" /> Admin
                    </Link>
                  )}
                  <button
                    onClick={() => { logout(); setSheetOpen(false); }}
                    className="mt-2 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 flex items-center gap-3 text-left"
                  >
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/talent-search" onClick={() => setSheetOpen(false)} data-testid="header-talent-search-public" className="px-4 py-3 rounded-lg text-white/85 hover:bg-white/5 flex items-center gap-3 border border-tv-accent/30">
                    <Trophy className="w-4 h-4 text-tv-accent" /> $50k Talent Search
                  </Link>
                  <Link to="/login" onClick={() => setSheetOpen(false)} className="px-4 py-3 rounded-lg border border-white/20 text-white text-center font-semibold">Sign in</Link>
                  <Link to="/subscribe" onClick={() => setSheetOpen(false)} className="mt-2 px-4 py-3 rounded-lg bg-tv-accent text-black text-center font-bold">Subscribe</Link>
                </>
              )}
            </nav>
          </SheetContent>
        </Sheet>

        {/* Brand */}
        <Link to="/" data-testid="brand-home-link" className="flex items-center shrink-0">
          <img
            src="/tapintv-logo-v2.png"
            alt="Tap IN TV Network"
            className="h-12 sm:h-14 md:h-16 lg:h-20 w-auto drop-shadow-[0_0_24px_rgba(250,204,21,0.4)] transition-all"
          />
        </Link>

        {/* Desktop nav — tighter spacing so everything fits at 1024-1440px */}
        <nav className="hidden md:flex items-center gap-2 lg:gap-3 xl:gap-4 text-[12px] lg:text-[13px] xl:text-sm text-white/85 ml-1">
          {kidMode.active ? (
            // Kid Mode: only the Kids link is visible. Exit via the /kids page PIN.
            <Link
              to="/kids"
              data-testid="nav-kids-only"
              className="whitespace-nowrap bg-yellow-300 hover:bg-yellow-200 text-black font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 text-[12px]"
            >
              <img src="/tapintv-kids-logo.png" alt="Kids" className="h-5 w-auto" />
              Kids only — tap to unlock
            </Link>
          ) : (
            <>
              {/* Go Live CTA — first position for creators, so it's the highest-signal action.
                  Routes to /studio/live (unified Director's Mode) so every creator hits the
                  SAME flow regardless of where they tapped — no more "Go Live" buttons that
                  scatter to /studio?tab=golive or /dashboard?go-live=1. */}
              {(role === "creator" || role === "premium_creator" || role === "admin") && (
                <Link
                  to="/studio/live"
                  data-testid="nav-go-live"
                  className="whitespace-nowrap bg-tv-live hover:bg-red-600 text-white font-bold px-2.5 lg:px-3 py-1.5 rounded-full flex items-center gap-1 text-[11px] lg:text-[12px] shadow-lg shadow-red-500/20"
                >
                  <Radio className="w-3 h-3 lg:w-3.5 lg:h-3.5 animate-pulse" />
                  <span className="hidden lg:inline">Go Live</span>
                  <span className="lg:hidden">Live</span>
                </Link>
              )}
              {(role === "creator" || role === "premium_creator" || role === "admin") && (
                <Link
                  to="/reels/create"
                  data-testid="nav-create-reel"
                  className="whitespace-nowrap bg-tv-accent hover:bg-yellow-400 text-black font-bold px-2.5 lg:px-3 py-1.5 rounded-full flex items-center gap-1 text-[11px] lg:text-[12px]"
                >
                  <Sparkles className="w-3 h-3 lg:w-3.5 lg:h-3.5" />
                  <span className="hidden lg:inline">Create Reel</span>
                  <span className="lg:hidden">Reel</span>
                </Link>
              )}
              {(role === "creator" || role === "premium_creator" || role === "admin") && (
                <Link
                  to="/reels/create?import=1"
                  data-testid="nav-import-social"
                  title="Import a reel from TikTok / Instagram / YouTube / X / Facebook / Vimeo"
                  className="whitespace-nowrap bg-white/10 hover:bg-white/20 text-white font-bold px-2.5 lg:px-3 py-1.5 rounded-full flex items-center gap-1 text-[11px] lg:text-[12px] border border-white/15"
                >
                  <LinkIcon className="w-3 h-3 lg:w-3.5 lg:h-3.5" />
                  <span className="hidden lg:inline">Import</span>
                </Link>
              )}
              {PRIMARY_NAV.map((n) => {
                if (n.dropdown) {
                  const items = n.dropdown === "movies" ? MOVIES_SUBCATS : MUSIC_SUBCATS;
                  const isActive = items.some((it) => it.to === loc.pathname);
                  return (
                    <DropdownMenu key={n.label}>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`nav-${n.label.toLowerCase()}-trigger`}
                          className={`whitespace-nowrap hover:text-white transition-colors flex items-center gap-1 outline-none ${isActive ? "text-tv-accent font-semibold" : ""}`}
                        >
                          {n.label} <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#0B1120] border-white/10 text-white min-w-56">
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.25em] text-tv-accent/80">{n.label} categories</DropdownMenuLabel>
                        <DropdownMenuSeparator className="bg-white/10" />
                        {items.map((it) => (
                          <DropdownMenuItem
                            key={it.to + it.label}
                            data-testid={`nav-${n.dropdown}-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            onSelect={() => nav(it.to)}
                            className="cursor-pointer"
                          >
                            {it.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                }
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    title={n.label}
                    className={`whitespace-nowrap hover:text-white transition-colors flex items-center gap-1 ${n.hideOnMd ? "hidden 2xl:flex" : ""} ${
                      loc.pathname === n.to ? "text-tv-accent font-semibold" : ""
                    }`}
                  >
                    {n.label === "Kids" && (
                      <img src="/tapintv-kids-logo.png" alt="Kids" className="inline-block h-5 lg:h-6 w-auto drop-shadow-[0_0_6px_rgba(250,204,21,0.6)]" />
                    )}
                    {n.iconOnly ? <span className="sr-only">{n.label}</span> : n.label}
                  </Link>
                );
              })}
            </>
          )}
          {!kidMode.active && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-more-trigger"
                className={`whitespace-nowrap hover:text-white transition-colors flex items-center gap-1 outline-none ${
                  MORE_NAV.some((n) => n.to === loc.pathname) ? "text-tv-accent font-semibold" : ""
                }`}
              >
                More <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#0B1120] border-white/10 text-white min-w-48">
              {MORE_NAV.map((n) => {
                const Icon = n.icon;
                return (
                  <DropdownMenuItem
                    key={n.to}
                    data-testid={`nav-more-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onSelect={() => nav(n.to)}
                    className="cursor-pointer"
                  >
                    {Icon && <Icon className="w-4 h-4 mr-2" />}
                    {n.label}
                  </DropdownMenuItem>
                );
              })}
              {(role === "creator" || role === "premium_creator" || role === "admin") && MORE_NAV_PREMIUM.map((n) => {
                const Icon = n.icon;
                return (
                  <DropdownMenuItem
                    key={n.to}
                    data-testid={`nav-more-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onSelect={() => nav(n.to)}
                    className="cursor-pointer"
                  >
                    {Icon && <Icon className="w-4 h-4 mr-2 text-tv-accent" />}
                    {n.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </nav>

        <div className="flex-1" />

        {/* Desktop search */}
        <form onSubmit={submitSearch} className="hidden lg:flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
          <Search className="w-4 h-4 text-white/60" />
          <input
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search films, creators, shows…"
            className="bg-transparent outline-none text-sm text-white placeholder:text-white/40 w-56 xl:w-72"
          />
        </form>

        {/* Mobile search button */}
        <button
          data-testid="mobile-search-btn"
          onClick={() => setMobileSearch(true)}
          className="lg:hidden w-10 h-10 rounded-full tv-glass flex items-center justify-center text-white shrink-0"
          aria-label="Search"
        >
          <Search className="w-4 h-4" />
        </button>

        {/* Language switcher */}
        <select
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          aria-label="Language"
          data-testid="lang-switcher"
          className="hidden sm:block bg-white/5 border border-white/10 rounded-full px-2 py-1 text-white text-xs font-semibold uppercase tracking-wider outline-none"
        >
          {languages.map((l) => <option key={l} value={l} className="bg-[#0B1120]">{l.toUpperCase()}</option>)}
        </select>

        {/* Cart icon */}
        <Link
          to="/cart"
          data-testid="header-cart-btn"
          className="relative w-10 h-10 rounded-full tv-glass flex items-center justify-center text-white shrink-0 hover:bg-white/10"
          aria-label="Cart"
        >
          <ShoppingBag className="w-4 h-4" />
          {cart?.count > 0 && (
            <span data-testid="cart-badge" className="absolute -top-1 -right-1 bg-tv-accent text-black text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center">
              {cart.count > 9 ? "9+" : cart.count}
            </span>
          )}
        </Link>

        {!user && (
          <>
            <Link
              to="/login"
              data-testid="header-login-link"
              className="hidden sm:inline text-sm text-white/85 hover:text-white whitespace-nowrap"
            >
              Sign in
            </Link>
            <Link
              to="/subscribe"
              data-testid="header-subscribe-btn"
              className="bg-tv-accent hover:bg-yellow-400 text-black font-bold text-xs sm:text-sm rounded-full px-3 sm:px-5 py-2 transition whitespace-nowrap"
            >
              Subscribe
            </Link>
          </>
        )}

        {user && (
          <NotificationBell />
        )}

        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-2 shrink-0">
                <Avatar className="w-9 h-9 border border-white/15">
                  <AvatarImage src={user.picture} />
                  <AvatarFallback className="bg-tv-brand text-black font-bold">{user.name?.[0]}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#0B1120] border-white/10 text-white min-w-56">
              <DropdownMenuLabel>
                <div className="font-semibold">{user.name}</div>
                <div className="text-xs text-white/60 font-normal">{user.email}</div>
                <div className="text-[10px] uppercase tracking-wider text-tv-brand mt-1">{user.role}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem data-testid="menu-my-dashboard" onSelect={() => nav("/dashboard")} className="cursor-pointer">
                <LayoutDashboard className="w-4 h-4 mr-2" /> My Dashboard
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-my-wallet" onSelect={() => nav("/wallet")} className="cursor-pointer">
                <Gift className="w-4 h-4 mr-2 text-emerald-300" /> My Wallet
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-my-timeline" onSelect={() => nav("/timeline")} className="cursor-pointer">
                <Sparkles className="w-4 h-4 mr-2" /> My Timeline
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-edit-profile" onSelect={() => nav("/profile/edit")} className="cursor-pointer">
                <UserCog className="w-4 h-4 mr-2" /> Edit Profile
              </DropdownMenuItem>
              {role === "viewer" && (
                <DropdownMenuItem data-testid="menu-apply-creator" onSelect={() => nav("/apply-creator")} className="cursor-pointer">
                  <Clapperboard className="w-4 h-4 mr-2" /> Become a Creator
                </DropdownMenuItem>
              )}
              {(role === "creator" || role === "premium_creator" || role === "admin") && (
                <DropdownMenuItem data-testid="menu-studio" onSelect={() => nav("/studio")} className="cursor-pointer">
                  <Clapperboard className="w-4 h-4 mr-2" /> Creator Studio
                </DropdownMenuItem>
              )}
              {(role === "premium_creator" || role === "admin") && (
                <DropdownMenuItem data-testid="menu-directors-mode" onSelect={() => nav("/studio/live")} className="cursor-pointer">
                  <Radio className="w-4 h-4 mr-2 text-tv-live" /> Director's Mode
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="menu-cockpit" onSelect={() => nav("/studio/assist")} className="cursor-pointer">
                <Radio className="w-4 h-4 mr-2 text-tv-accent" /> Assistant Cockpit
              </DropdownMenuItem>
              {role === "admin" && (
                <DropdownMenuItem data-testid="menu-admin" onSelect={() => nav("/admin")} className="cursor-pointer">
                  <ShieldCheck className="w-4 h-4 mr-2" /> Admin Console
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="bg-white/10" />
              {user.email_verification_required && !user.email_verified_at && (
                <DropdownMenuItem
                  data-testid="menu-resend-verification"
                  onSelect={async (e) => {
                    e?.preventDefault?.();
                    try {
                      const { data } = await api.post("/auth/resend-verification", { email: user.email });
                      if (data?.rate_limited) {
                        toast("We just sent one — check spam, or try again in 60 seconds.", { duration: 4500 });
                      } else {
                        toast.success("Confirmation email sent. Check your inbox.");
                      }
                    } catch {
                      toast.error("Couldn't resend right now. Try again in a minute.");
                    }
                  }}
                  className="cursor-pointer text-amber-300"
                >
                  <Bell className="w-4 h-4 mr-2" /> Resend verification email
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="menu-notification-settings" onSelect={() => nav("/settings/notifications")} className="cursor-pointer">
                <Bell className="w-4 h-4 mr-2" /> Notification settings
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-logout" onSelect={logout} className="cursor-pointer text-red-400">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Mobile search drawer (top slide) */}
      {mobileSearch && (
        <div className="lg:hidden bg-black/95 backdrop-blur-md border-t border-white/10 px-3 py-3 flex items-center gap-2">
          <form onSubmit={submitSearch} className="flex-1 flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-2">
            <Search className="w-4 h-4 text-white/60" />
            <input
              autoFocus
              data-testid="mobile-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search films, creators, shows…"
              className="bg-transparent outline-none text-sm text-white placeholder:text-white/40 flex-1"
            />
          </form>
          <button
            onClick={() => setMobileSearch(false)}
            aria-label="Close search"
            className="w-10 h-10 rounded-full tv-glass flex items-center justify-center text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </header>
  );
}
